import { useEffect, useState } from "react";
import { X, Play, Crown } from "lucide-react";

const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return yt ? `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0` : url;
};

const isDirectVideo = (url) => /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url || "");

export default function TrailerModal({ show, onClose, isPremiumUser, onUpgrade }) {
  const [adDismissed, setAdDismissed] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const embed = embedUrl(show.trailer_url);
  const needsPremium = show.access === "premium" && !isPremiumUser;

  return (
    <div data-testid="trailer-modal" className="fixed inset-0 z-[90] flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" data-testid="trailer-modal-backdrop" onClick={onClose} />
      <div className="relative w-full max-w-5xl">
        <div className="flex items-center justify-between pb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] text-gold-500">Now Previewing</p>
            <h3 className="mt-1 font-display text-2xl font-bold text-stone-50">
              {show.title}
              {show.access === "premium" && (
                <span className="ml-3 rounded-full bg-gradient-to-r from-gold-600 to-gold-400 px-3 py-1 align-middle text-[9px] font-bold uppercase tracking-[0.16em] text-ink-950">
                  Premium
                </span>
              )}
            </h3>
          </div>
          <button
            data-testid="trailer-modal-close-button"
            aria-label="Close trailer"
            onClick={onClose}
            className="rounded-full border border-stone-700 p-2.5 text-stone-300 transition-all duration-300 hover:border-gold-500/60 hover:text-gold-400"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {needsPremium ? (
          <div data-testid="premium-gate" className="flex flex-col items-center gap-4 rounded-2xl border border-gold-500/25 bg-ink-900 px-6 py-14 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10">
              <Crown className="h-7 w-7 text-gold-400" />
            </span>
            <p className="font-display text-lg font-bold uppercase tracking-[0.2em] text-gold-300">Premium Show</p>
            <p className="max-w-md text-sm leading-relaxed text-stone-400">
              Subscribe to VIEWNA Premium at ₹199/month to watch {show.title} — ad-free streaming, all Premium &amp;
              Exclusive shows and HD playback.
            </p>
            <button
              data-testid="premium-gate-subscribe-button"
              onClick={onUpgrade}
              className="mt-2 inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-gold-600 via-gold-400 to-gold-600 px-8 py-3.5 text-[12px] font-bold uppercase tracking-[0.2em] text-ink-950 transition-all duration-300 hover:shadow-[0_0_28px_rgba(212,175,55,0.45)]"
            >
              <Crown className="h-4 w-4" /> Subscribe Now — ₹199/month
            </button>
          </div>
        ) : (
          <>
            {!isPremiumUser && !adDismissed && (
              <div data-testid="ad-banner" className="mb-3 flex items-center justify-between gap-3 rounded-lg border border-gold-500/30 bg-gold-500/10 px-4 py-2">
                <p className="text-[11px] uppercase tracking-[0.16em] text-gold-300">
                  <span className="mr-2 rounded bg-gold-500 px-1.5 py-0.5 text-[9px] font-bold text-ink-950">AD</span>
                  Ads stay away with VIEWNA Premium — ₹199/month
                </p>
                <div className="flex items-center gap-3">
                  <button data-testid="ad-upgrade-button" onClick={onUpgrade} className="rounded-full bg-gradient-to-r from-gold-600 to-gold-400 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-950">
                    Upgrade
                  </button>
                  <button data-testid="ad-dismiss-button" aria-label="Dismiss ad" onClick={() => setAdDismissed(true)} className="text-base text-stone-500 transition-colors hover:text-stone-300">
                    ×
                  </button>
                </div>
              </div>
            )}
            <div className="aspect-video overflow-hidden rounded-2xl border border-gold-500/25 bg-ink-900 shadow-[0_0_80px_rgba(212,175,55,0.12)]">
              {show.trailer_url && isDirectVideo(show.trailer_url) ? (
                <video
                  src={show.trailer_url}
                  controls
                  autoPlay
                  data-testid="trailer-video"
                  className="h-full w-full bg-black"
                />
              ) : embed ? (
                <iframe
                  src={embed}
                  title={`${show.title} trailer`}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
                  <span className="flex h-20 w-20 items-center justify-center rounded-full border border-gold-500/40 bg-gold-500/10">
                    <Play className="h-8 w-8 fill-current text-gold-400" />
                  </span>
                  <p className="font-display text-lg font-bold uppercase tracking-[0.35em] text-gold-300">
                    Official Trailer Dropping Soon
                  </p>
                  <p className="max-w-sm text-sm leading-relaxed text-stone-500">
                    {show.title} — only on VIEWNA. Stay tuned, the spotlight is about to hit.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
